// Filename: src/utils/apiUtils.js
// Current Date and Time (UTC): 2025-02-04 13:47:25
// Current User's Login: raghavkunkee

/**
 * API Utilities
 * Contains utility functions and constants for API interactions.
 * Features:
 * - API response handling with fallbacks
 * - Data structure definitions
 * - Currency formatting
 * - Token generation
 */

import numeral from 'numeral';

/**
 * Default data options for the application
 */
export const defaultOptions = {
    dataTypes: [
        { value: "GROUP_METADATA", label: "Metadata" },
        { value: "GROUP_ALL", label: "All Groups" },
        { value: "GROUP_BASIC", label: "Basic Information" }
    ],
    markets: [
        { value: "A21TJRUUN4KGV", label: "India (IN)" },
        { value: "ATVPDKIKX0DER", label: "United States (US)" },
        { value: "A1F83G8C2ARO7P", label: "United Kingdom (UK)" }
    ],
    requestTypes: [
        { value: "REFRESH_DATA", label: "Fetch" },
        { value: "GENERATE_REPORT", label: "Generate Report" },
        { value: "REFRESH_DATA_AND_GENERATE_REPORT", label: "Fetch & Generate Report" }
    ]
};

/**
 * Default data structures for API responses
 */
export const defaultBookData = {
    _embedded: {
        productVOList: []
    },
    page: {
        totalPages: 1,
        totalElements: 0
    }
};

export const defaultFieldGroupsData = {
    _embedded: {
        fieldGroupVOList: []
    }
};

/**
 * Field group mappings for report types
 */
export const reportFieldGroups = {
    GROUP_ALL: "ALL",
    GROUP_METADATA: "Meta data",
    GROUP_DIMENSIONS: "Dimensions (Product and Package)",
    GROUP_RANKING: "Ranking (ALL)",
    GROUP_RANKING_BOOKS: "Ranking (Books)",
    GROUP_OFFERS: "Pricing (BuyBox and Lowest)",
    GROUP_IMAGES: "Images",
    GROUP_BB_OFFERS: "Pricing (BuyBox)",
    GROUP_LP_OFFERS: "Pricing (Lowest)",
    GROUP_METADATA_NEW: "Metadata (new)",
    GROUP_BO_CODE_STOCK: "BO Stock File (new)"
};

/**
 * Format currency amount
 * @param {number} amount - Amount to format
 * @param {string} currency - Currency code
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount, currency) => {
    if (!amount) return 'NA';
    return `${currency}${numeral(amount).format('0,0')}`;
};

/**
 * Generates API token using current date
 * @returns {string} Encoded API token
 */
export const generateApiToken = () => {
    const date = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    return btoa(`X-BW-API-TOKEN-${date}`);
};

/**
 * Validates API response data structure
 * @param {Object} data - API response data
 * @returns {boolean} true if valid, false otherwise
 */
const isValidApiResponse = (data) => {
    if (data?._embedded) {
        return true;
    }
    
    return data && 
           typeof data === 'object' && 
           !Array.isArray(data) &&
           Object.keys(data).length > 0;
};

/**
 * Enhanced fetch function with improved error handling and loading states
 * @param {string} url - API endpoint URL
 * @param {Object} defaultData - Default data to return if API fails
 * @param {Object} options - Additional fetch options
 * @param {string} token - Keycloak token
 * @returns {Promise<Object>} API response or default data
 */
export const fetchWithFallback = async (url, defaultData, options = {}, token = '') => {
    const headers = {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'Authorization': token ? `Bearer ${token}` : '',
        'X-BW-API-TOKEN': generateApiToken(),
        ...options.headers
    };

    try {
        const response = await fetch(url, {
            ...options,
            headers
        });

        if (!response.ok) {
            console.warn(`API returned status ${response.status} for ${url}`);
            return defaultData;
        }

        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
            console.warn(`Invalid content type ${contentType} from API`);
            return defaultData;
        }

        const data = await response.json();
        
        if (url.includes('/v1/products') && data?._embedded?.productVOList) {
            return data;
        }

        if (!isValidApiResponse(data)) {
            console.warn('Invalid data structure received from API');
            return defaultData;
        }

        return data;

    } catch (error) {
        console.error(`API Error for ${url}:`, error.message);
        return defaultData;
    }
};